import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { AnimationController, Platform } from '@ionic/angular';
import { buildRouteSlideAnimation } from 'src/shared/animations/route-slide.animation';
import { TranslateService } from '@ngx-translate/core';
import { PushManagerProvider } from 'src/providers/push/push-manager.provider';
import { availableLanguages, defaultLanguage } from 'src/assets/i18n/languages';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { StorageProvider } from 'src/providers/storage.provider';
import { TabsProvider } from 'src/providers/tabs/tabs.provider';
import { ThemeService, ThemeStyle } from 'src/providers/theme.service';
import { PushProvider } from 'src/providers/push/push.provider';
import { SplashScreenProvider } from 'src/providers/splash-screen.provider';
import { SentryLogProvider } from 'src/providers/sentry/sentry.provider';
import { ScreenAnimationProvider } from 'src/providers/screen-animation-provider';
import { Subscription } from 'rxjs';

// Register swiper for Presence Monitroing page
import { register } from 'swiper/element/bundle';
import { iconList } from 'src/constants/ionicons-import';
register();

// Import ionic icons
import { addIcons } from 'ionicons';
addIcons(iconList);

@Component({
  selector: 'rota-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  private themeSub: Subscription;

  constructor(
    private platform: Platform,
    private pushService: PushProvider,
    private pushManagerProvider: PushManagerProvider,
    public screenAnimationProvider: ScreenAnimationProvider,
    private translate: TranslateService,
    private tabsProvider: TabsProvider,
    private storageProvider: StorageProvider,
    private splash: SplashScreenProvider,
    private sentry: SentryLogProvider,
    private themeService: ThemeService,
    animationCtrl: AnimationController
  ) {
    this.routeAnimation = buildRouteSlideAnimation(animationCtrl);
  }

  protected routeAnimation: ReturnType<typeof buildRouteSlideAnimation>;

  get isShake() {
    return this.screenAnimationProvider.isShake();
  }

  get isWave() {
    return this.screenAnimationProvider.isWave();
  }

  isLightTheme = signal<boolean>(false);

  async ngOnInit() {
    this.splash.showWithLoading();

    this.sentry.init();

    this.tabsProvider.subscribeToBackButtonEvent();
    this.tabsProvider.subscribeToRouteChange();

    this.initializeApp();
  }

  initializeApp() {
    this.platform.ready().then(async () => {
      this.setupTheme();

      this.setupAppLanguage();

      if (!this.storageProvider.isDesktop()) {
        await ScreenOrientation.lock({ orientation: 'portrait' });

        // Listen push notification (only mobile devices)
        this.pushService.listen();
        this.pushManagerProvider.init();
      }
    });
  }

  async setupTheme() {
    const storageTheme = await this.themeService.getStorageTheme();
    await this.themeService.changeTo(storageTheme);

    // Initialize isLightTheme with current value
    this.isLightTheme.set(this.themeService.current === ThemeStyle.light);

    // Subscribe to theme changes
    this.themeSub = this.themeService.themeChanged$.subscribe(() => {
      this.isLightTheme.set(this.themeService.current === ThemeStyle.light);
    });

    if (storageTheme === ThemeStyle.system) {
      await this.themeService.subscribeSystemThemeChange();
    }
  }

  setupAppLanguage() {
    const browserLang = this.translate.getBrowserLang();
    if (browserLang) {
      const language = availableLanguages.some((x) => x.code == browserLang)
        ? browserLang
        : defaultLanguage;
      this.translate.setDefaultLang(language);
    } else {
      this.translate.setDefaultLang(defaultLanguage);
    }
  }

  ngOnDestroy() {
    if (this.themeSub) {
      this.themeSub.unsubscribe();
    }
  }
}
